import { Check } from 'lucide-react'
import { Card, Row, Tag } from '../ui'
import { setRegionOverride, useCurrentRegion } from '../lib/currentRegion'
import { useProfile } from '../lib/profile'
import { homeRegionOf, useRegionsNow } from '../lib/regions'

/**
 * Account → Region (cities plan C2.2, D2). One row per region, then *Follow
 * my stays*.
 *
 * Tapping a region sets this device's override; tapping *Follow my stays*
 * clears it, and the app shows the home region, or the region of whichever
 * stay covers today. The choice lives in this browser (`currentRegion.ts`),
 * not in the profile, so a phone in Concord and a laptop at home can look at
 * two cities.
 *
 * It changes what the whole app is looking at, so the first visit to a region
 * is a cold load: two pages for Today, other screens loading until the rest
 * arrives. That is the ordinary cold-load behaviour and is not hidden. A
 * region with nothing harvested yet shows its empty state honestly.
 *
 * The check marks the *choice*, not the outcome: a check on a region row means
 * "you chose this", a check on *Follow my stays* means "you did not". While
 * following, that row says what it is showing.
 */
export function RegionSection({ onChosen }: { onChosen?: () => void }) {
  const regions = useRegionsNow()
  const { region, source, stay } = useCurrentRegion()
  const homeId = homeRegionOf(useProfile())
  const following = source !== 'override'

  const list = [...regions.regions.values()].sort((a, b) =>
    Number(b.id === homeId) - Number(a.id === homeId) || a.name.localeCompare(b.name))

  function choose(id: string | null) {
    setRegionOverride(id)
    onChosen?.()
  }

  return (
    <section>
      <div className="rowhead"><h2>Region</h2></div>
      <Card>
        {list.map(r => {
          const home = r.id === homeId
          const staying = !!stay && stay.region_id === r.id && !home
          const chosen = !following && r.id === region.id
          return (
            <Row
              key={r.id}
              className="u-row--list u-row--flush"
              leading={null}
              title={r.name}
              meta={home || staying
                ? <>{home && <Tag>Home</Tag>}{staying && <Tag tone="accent">Staying</Tag>}</>
                : null}
              trailing={chosen ? <Check size={18} aria-hidden="true" /> : null}
              aria-current={chosen ? 'true' : undefined}
              onPress={() => choose(r.id)}
            />
          )
        })}
        <Row
          className="u-row--list u-row--flush"
          leading={null}
          title="Follow my stays"
          meta={following ? `Showing ${region.name}` : null}
          trailing={following ? <Check size={18} aria-hidden="true" /> : null}
          aria-current={following ? 'true' : undefined}
          onPress={() => choose(null)}
        />
      </Card>
    </section>
  )
}
